import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { ColumnType } from "antd/es/table/interface";
import { DateTime } from "luxon";
import { InfoCircleOutlined } from "@ant-design/icons";

import QueryTable from "@/components_v2/Tables/QueryTable";
import BaseBadge from "@/components_v2/Badge/BaseBadge";
import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import { ApplicationStatuses } from "@/shared/utils/constants";
import {
  ApplicationFiltersState,
  ApplicationTableProps,
} from "@/features/organizations/profile/types";
import { useLazyGetApplicationsQuery } from "@/redux/service";
import { ApplicationResponse } from "@/redux/applications/types";
import ApplicationFilters from "@/features/organizations/profile/components/ApplicationFilters";
import ApplicationDetailModal from "@/features/organizations/profile/components/ApplicationDetailModal";

import "../styles.css";

const ApplicationActionsCell = ({
  application,
}: {
  application: ApplicationResponse;
}) => {
  const { t } = useTranslation();
  const [openDetailModal, setOpenDetailModal] = useState(false);

  return (
    <>
      <BaseButton
        variant={BUTTON_VARIANTS.LINK}
        size={BUTTON_SIZES.SMALL}
        onClick={() => setOpenDetailModal(true)}
      >
        {t("organizations.labels.viewApplication")}
      </BaseButton>

      <ApplicationDetailModal
        open={openDetailModal}
        setOpen={setOpenDetailModal}
        application={application}
        onUpdated={() => undefined}
      />
    </>
  );
};

const ApplicationTable = ({
  columnNames,
  filterNames,
  defaultFilters = {},
  additionalColumns,
  ...props
}: ApplicationTableProps<ApplicationResponse>) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [filters, setFilters] =
    useState<Partial<ApplicationFiltersState>>(defaultFilters);

  const settingsTabSearch = useMemo(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", "settings");
    return `?${newParams.toString()}`;
  }, [searchParams]);

  const columns: Record<
    string,
    ColumnType<ApplicationResponse>
  > = useMemo(() => {
    const predefinedColumns: Record<string, ColumnType<ApplicationResponse>> = {
      firstName: {
        title: t("organizations.labels.firstName"),
        key: "firstName",
        dataIndex: "firstName",
      },
      lastName: {
        title: t("organizations.labels.lastName"),
        key: "lastName",
        dataIndex: "lastName",
      },
      email: {
        title: t("organizations.labels.email"),
        key: "email",
        dataIndex: "email",
      },
      status: {
        title: t("organizations.labels.status"),
        key: "status",
        dataIndex: "status",
        render: (value: string) => {
          const status = ApplicationStatuses.getItemByValue(value);
          return (
            <BaseBadge
              backgroundColor={status?.backgroundColor}
              fontColor={status?.color}
              borderColor={status?.color}
            >
              {status?.label || value}
            </BaseBadge>
          );
        },
      },
      createdAt: {
        title: t("organizations.labels.appliedDate"),
        key: "createdAt",
        dataIndex: "createdAt",
        sorter: true,
        defaultSortOrder: "descend",
        render: (value: string) =>
          DateTime.fromISO(value).toFormat("dd MMMM yyyy"),
      },
      actions: {
        title: t("organizations.labels.actions"),
        key: "actions",
        render: (application: ApplicationResponse) => (
          <ApplicationActionsCell application={application} />
        ),
      },
    };
    return { ...predefinedColumns, ...(additionalColumns || {}) };
  }, [additionalColumns]);

  const mappedColumns = useMemo(
    () =>
      columnNames.map(
        (columnKey) => columns[columnKey] as ColumnType<ApplicationResponse>,
      ),
    [columns, columnNames],
  );

  return (
    <div className="table-container-layout">
      <div className="applications-domain-help-text flex gap-100">
        <InfoCircleOutlined className="applications-domain-help-text-icon" />
        <p className="body-s-regular">
          <Trans
            i18nKey="organizations.labels.applicationsDomainHelpText"
            components={{
              settingsLink: (
                <Link
                  to={{ search: settingsTabSearch }}
                  replace
                  className="applications-domain-help-text-link"
                />
              ),
            }}
          />
        </p>
      </div>

      <ApplicationFilters
        filterNames={filterNames}
        setFilters={setFilters}
        defaultFilters={defaultFilters}
      />

      <QueryTable<ApplicationResponse>
        query={useLazyGetApplicationsQuery}
        filters={filters}
        columns={mappedColumns}
        {...props}
      />
    </div>
  );
};

export default ApplicationTable;
